
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Button from '../components/Button';
import Input from '../components/Input';
import PropertyCard from '../components/PropertyCard';
import imageCompression from 'browser-image-compression';
import { BANGALORE_AREAS } from '../constants/bangaloreAreas';
import { PROPERTY_AMENITIES, PROPERTY_RULES } from '../constants/propertyOptions';
import './MyListings.css';

const MyListings = () => {
    const [myProperties, setMyProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);

    const [listingImages, setListingImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [existingImages, setExistingImages] = useState([]);

    // Posted By Modal State
    const [showPostedByModal, setShowPostedByModal] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(false);

    // Custom Input State
    const [customAmenity, setCustomAmenity] = useState('');
    const [customRule, setCustomRule] = useState('');

    const resolveAreaId = async (locationName) => {
        const normalized = (locationName || '').trim();
        if (!normalized) return null;

        const { data, error } = await supabase
            .from('areas')
            .select('id')
            .eq('name', normalized)
            .eq('city', 'Bangalore')
            .eq('state', 'Karnataka')
            .maybeSingle();

        if (error) throw error;
        return data?.id || null;
    };

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        location: '', // Will be dropdown
        listing_type: 'Rent', // Default
        bedrooms: '',
        bathrooms: '',
        area_sqft: '',
        property_type: 'Apartment',
        furnishing: 'Unfurnished',
        balconies: '',
        floor_number: '',
        total_floors: '',
        facing: '',
        age_of_property: '',
        availability_date: '',
        security_deposit: '',
        maintenance_charge: '',
        maintenance_frequency: 'Monthly',
        negotiable: false,
        amenities: [],
        rules: []
    });

    useEffect(() => {
        const getUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            setUser(user);
            if (user) {
                // Fetch user role
                const { data: profile } = await supabase
                    .from('users')
                    .select('role')
                    .eq('id', user.id)
                    .single();

                if (profile) setUserRole(profile.role);
                fetchMyProperties(user.id);
            }
        };
        getUser();
    }, []);

    const fetchMyProperties = async (userId) => {
        try {
            const { data, error } = await supabase
                .from('property')
                .select('*, areas(name, city)')
                .eq('owner_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            const mapped = (data || []).map(p => ({
                ...p,
                price: p.expected_rent, // Map expected_rent -> price
                images: p.media_url || [], // Map media_url -> images
                bedrooms: p.bhk,
                bathrooms: p.bathrooms,
                area_sqft: p.sqft,
                location: p.areas ? p.areas.name : ''
            }));

            setMyProperties(mapped);
        } catch (error) {
            console.error('Error fetching properties:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        if (!value) return '';
        // Remove non-digits
        const number = value.replace(/[^0-9]/g, '');
        // Format as Indian Currency (or generic)
        return new Intl.NumberFormat('en-IN').format(number);
    };

    const handleCurrencyChange = (e) => {
        const { name, value } = e.target;
        // Remove existing commas to get raw number string
        const rawValue = value.replace(/,/g, '');
        if (isNaN(rawValue)) return; // valid number only

        setFormData({
            ...formData,
            [name]: formatCurrency(rawValue)
        });
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleImageSelect = (e) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setListingImages(files);

            // Create previews
            const newPreviews = files.map(file => URL.createObjectURL(file));
            // revoke old ones to avoid memory leaks? usually handled but ensuring cleanup is hard in simple handler
            // Just replace state for now
            setImagePreviews(newPreviews);
        }
    };



    const handleAddCustom = (type) => {
        if (type === 'amenity' && customAmenity.trim()) {
            if (!formData.amenities.includes(customAmenity.trim())) {
                setFormData({
                    ...formData,
                    amenities: [...formData.amenities, customAmenity.trim()]
                });
            }
            setCustomAmenity('');
        } else if (type === 'rule' && customRule.trim()) {
            if (!formData.rules.includes(customRule.trim())) {
                setFormData({
                    ...formData,
                    rules: [...formData.rules, customRule.trim()]
                });
            }
            setCustomRule('');
        }
    };

    const handleEdit = (property) => {
        setEditingId(property.id);
        const formatVal = (val) => val ? formatCurrency(val.toString()) : '';

        setFormData({
            title: property.title,
            description: property.description || '',
            price: formatVal(property.price),
            location: property.location,
            listing_type: property.listing_type || 'Rent',
            bedrooms: property.bedrooms,
            bathrooms: property.bathrooms,
            area_sqft: property.area_sqft,
            property_type: property.property_type,
            furnishing: property.furnishing,
            balconies: property.balconies,
            floor_number: property.floor_number,
            total_floors: property.total_floors,
            facing: property.facing,
            age_of_property: property.age_of_property,
            availability_date: property.availability_date || '',
            security_deposit: property.security_deposit ? property.security_deposit.toString() : '',
            maintenance_charge: formatVal(property.maintenance_charge),
            maintenance_frequency: property.maintenance_frequency || 'Monthly',
            negotiable: property.negotiable || false,
            amenities: Array.isArray(property.amenities) ? property.amenities : [],
            rules: Array.isArray(property.rules) ? property.rules : []
        });

        setExistingImages(property.images || []);
        setImagePreviews(property.images || []);
        setListingImages([]); // Clear new uploads selection
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setExistingImages([]);
        setListingImages([]);
        setImagePreviews([]);
        setFormData({
            title: '', description: '', price: '', location: '', listing_type: 'Rent',
            bedrooms: '', bathrooms: '', area_sqft: '', property_type: 'Apartment',
            furnishing: 'Unfurnished', balconies: '', floor_number: '', total_floors: '',
            facing: '', age_of_property: '', availability_date: '',
            security_deposit: '', maintenance_charge: '', maintenance_frequency: 'Monthly',
            negotiable: false, amenities: [], rules: []
        });
        setShowPostedByModal(false);
        setPendingSubmission(false);
    };

    const handleInitialSubmit = (e) => {
        e.preventDefault();
        if (!user) return;

        if (listingImages.length === 0 && existingImages.length === 0) {
            alert("Please upload at least one image for your property.");
            return;
        }

        // Show Posted By Modal
        setShowPostedByModal(true);
    };

    const handleFinalSubmit = async (postedBy) => {
        setShowPostedByModal(false);
        setLoading(true);

        try {
            // 1. Upload Images
            const uploadedUrls = [];
            const compressionOptions = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                initialQuality: 0.7
            };

            for (const file of listingImages) {
                let uploadFile = file;
                try {
                    // Compress
                    const compressedFile = await imageCompression(file, compressionOptions);
                    uploadFile = compressedFile;
                } catch (cErr) {
                    console.error("Compression failed, using original", cErr);
                }

                const fileExt = uploadFile.name.split('.').pop();
                const fileName = `${user.id}/${Date.now()}_${Math.random()}.${fileExt}`;
                const { error: uploadError } = await supabase.storage
                    .from('property-media')
                    .upload(fileName, uploadFile);

                if (uploadError) {
                    console.error('Error uploading image:', uploadError);
                    continue;
                }

                const { data: { publicUrl } } = supabase.storage
                    .from('property-media')
                    .getPublicUrl(fileName);

                uploadedUrls.push(publicUrl);
            }

            // Helper to clean currency string
            const cleanAmount = (val) => val ? parseFloat(val.replace(/,/g, '')) : null;

            const propertyPayload = {
                owner_id: user.id,
                title: formData.title,
                description: formData.description,
                area_id: await resolveAreaId(formData.location),
                address_line: formData.location,
                expected_rent: cleanAmount(formData.price),
                security_deposit: formData.security_deposit ? parseInt(formData.security_deposit) : null,
                maintenance_charge: cleanAmount(formData.maintenance_charge),
                maintenance_frequency: formData.maintenance_frequency,
                negotiable: formData.negotiable || false,
                bhk: parseInt(formData.bedrooms),
                bathrooms: parseInt(formData.bathrooms),
                balconies: formData.balconies ? parseInt(formData.balconies) : null,
                floor_number: formData.floor_number ? parseInt(formData.floor_number) : null,
                total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
                sqft: parseInt(formData.area_sqft),
                facing: formData.facing,
                age_of_property: formData.age_of_property ? parseInt(formData.age_of_property) : null,
                availability_date: formData.availability_date || null,
                property_type: formData.property_type,
                listing_type: formData.listing_type,
                posted_by: postedBy, // Set from modal
                furnishing: formData.furnishing,
                status: 'active',
                amenities: formData.amenities,
                rules: formData.rules
            };

            if (editingId) {
                // UPDATE
                const finalImages = [...existingImages, ...uploadedUrls];
                const { error: updateError } = await supabase
                    .from('property')
                    .update({ ...propertyPayload, media_url: finalImages })
                    .eq('id', editingId);

                if (updateError) throw updateError;

                alert("Property updated successfully!");
                // Refresh list
                const updatedList = myProperties.map(p =>
                    p.id === editingId ? { ...p, ...propertyPayload, images: finalImages, media_url: finalImages, price: propertyPayload.expected_rent, location: formData.location } : p
                );
                setMyProperties(updatedList);

            } else {
                // INSERT
                const { data: propertyData, error: propertyError } = await supabase
                    .from('property')
                    .insert([{ ...propertyPayload, media_url: uploadedUrls }])
                    .select()
                    .single();

                if (propertyError) throw propertyError;

                const newProperty = {
                    ...propertyData,
                    price: propertyData.expected_rent,
                    images: propertyData.media_url || [],
                    bedrooms: propertyData.bhk,
                    bathrooms: propertyData.bathrooms,
                    area_sqft: propertyData.sqft,
                    location: formData.location
                };
                setMyProperties([newProperty, ...myProperties]);
                alert("Property listed successfully!");
            }

            resetForm();
        } catch (error) {
            alert('Error saving property: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, e) => {
        e.preventDefault(); // prevent link navigation
        if (!window.confirm("Are you sure you want to delete this listing?")) return;

        try {
            // 1. Fetch property to get image URLs
            const { data: property, error: fetchError } = await supabase
                .from('property')
                .select('media_url')
                .eq('id', id)
                .single();

            if (fetchError) throw fetchError;

            // 2. Delete from Storage
            const mediaUrls = property.media_url || [];
            if (mediaUrls.length > 0) {
                // Extract paths from public URL
                // Format: .../storage/v1/object/public/property-media/{userId}/{fileName}
                // We need: {userId}/{fileName}
                const paths = mediaUrls.map(url => {
                    const parts = url.split('/property-media/');
                    return parts.length > 1 ? parts[1] : null;
                }).filter(path => path !== null);

                if (paths.length > 0) {
                    const { error: storageError } = await supabase.storage
                        .from('property-media')
                        .remove(paths);

                    if (storageError) console.error("Error deleting images:", storageError);
                }
            }

            // 3. Delete from DB
            const { error } = await supabase
                .from('property')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setMyProperties(myProperties.filter(p => p.id !== id));
            alert("Property deleted successfully.");
        } catch (error) {
            console.error('Error deleting property:', error);
            alert("Failed to delete property: " + error.message);
        }
    };

    return (
        <div className="page-container my-listings-page">
            <SEO
                title="My Listings"
                description="Manage your property listings on The Rent Company."
                path="/my-listings"
            />
            <Navbar transparent />

            {/* Gradient Header Background */}
            <div className="listings-header-bg">
                <div className="listings-header-content">
                    <div>
                        <h1>My Listings</h1>
                        <p className="subtitle">Manage your property portfolio</p>
                    </div>
                    <div className="header-actions">
                        {myProperties.length > 0 && (
                            <span className="stats-badge">
                                {myProperties.length} Active Listing{myProperties.length !== 1 ? 's' : ''}
                            </span>
                        )}
                        <Button onClick={() => {
                            if (userRole === 'tenant') {
                                alert("As a Tenant, you cannot list properties. Please switch your account to Broker or Owner in the Profile page.");
                                return;
                            }
                            if (showForm) {
                                resetForm();
                            } else {
                                setShowForm(true);
                            }
                        }}>
                            {showForm ? 'Cancel' : '+ Add New Property'}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="main-content">
                {/* Posted By Modal */}
                {showPostedByModal && (
                    <div className="modal-overlay">
                        <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                            <h3>Posting as?</h3>
                            <p style={{ marginBottom: '1.5rem', color: '#666' }}>Are you listing this property as an Owner or a Broker?</p>
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                <Button onClick={() => handleFinalSubmit('Owner')}>Owner</Button>
                                <Button className="secondary" onClick={() => handleFinalSubmit('Broker')}>Broker</Button>
                            </div>
                            <button
                                onClick={() => setShowPostedByModal(false)}
                                style={{ marginTop: '1rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: '#888' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {showForm && (
                    <div className="create-form-container fade-in">
                        <div className="form-header">
                            <h2>{editingId ? 'Edit Property' : 'Add New Property'}</h2>
                            <p>{editingId ? 'Update your property details' : 'Fill in the details to publish your listing'}</p>
                        </div>

                        <form onSubmit={handleInitialSubmit} className="create-form">
                            {/* Section 1: Basic Information */}
                            <div className="form-section">
                                <h3 className="section-title">
                                    <span className="section-number">1</span> Basic Information
                                </h3>

                                <Input label="Property Title" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g. Spacious 2BHK in Indiranagar" required />
                                <div className="form-row">
                                    <div className="input-group">
                                        <label className="input-label">Location</label>
                                        <select
                                            name="location"
                                            className="input-field select-field"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Location</option>
                                            {BANGALORE_AREAS.map(area => (
                                                <option key={area} value={area}>{area}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">Listing Type</label>
                                        <select
                                            name="listing_type"
                                            className="input-field select-field"
                                            value={formData.listing_type}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Rent">Rent</option>
                                            <option value="Lease">Lease</option>
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">Property Type</label>
                                        <select
                                            name="property_type"
                                            className="input-field select-field"
                                            value={formData.property_type}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Apartment">Apartment</option>
                                            <option value="Villa">Villa</option>
                                            <option value="Independent House">Independent House</option>
                                            <option value="PG">PG</option>
                                            <option value="Commercial">Commercial</option>
                                            <option value="Land">Land</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Description (Optional)</label>
                                    <textarea
                                        name="description"
                                        className="input-field textarea-field"
                                        rows="4"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Describe the property features, nearby landmarks, etc."
                                    ></textarea>
                                </div>
                            </div>

                            {/* Section 2: Property Specifications */}
                            <div className="form-section">
                                <h3 className="section-title">
                                    <span className="section-number">2</span> Specifications
                                </h3>

                                <div className="grid-3-col">
                                    <Input label="Bedrooms (BHK)" name="bedrooms" type="number" value={formData.bedrooms} onChange={handleInputChange} required />
                                    <Input label="Bathrooms" name="bathrooms" type="number" value={formData.bathrooms} onChange={handleInputChange} required />
                                    <Input label="Balconies (Optional)" name="balconies" type="number" value={formData.balconies} onChange={handleInputChange} />
                                </div>

                                <div className="grid-3-col">
                                    <Input label="Area (sqft)" name="area_sqft" type="number" value={formData.area_sqft} onChange={handleInputChange} required />
                                    <div className="input-group">
                                        <label className="input-label">Furnishing</label>
                                        <select
                                            name="furnishing"
                                            className="input-field select-field"
                                            value={formData.furnishing}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Unfurnished">Unfurnished</option>
                                            <option value="Semi">Semi-Furnished</option>
                                            <option value="Fully">Fully Furnished</option>
                                        </select>
                                    </div>
                                    <Input label="Facing (Optional)" name="facing" value={formData.facing} onChange={handleInputChange} placeholder="e.g. East" />
                                </div>

                                <div className="grid-3-col">
                                    <Input label="Floor No. (Optional)" name="floor_number" type="number" value={formData.floor_number} onChange={handleInputChange} />
                                    <Input label="Total Floors (Optional)" name="total_floors" type="number" value={formData.total_floors} onChange={handleInputChange} />
                                    <Input label="Age (Years) (Optional)" name="age_of_property" type="number" value={formData.age_of_property} onChange={handleInputChange} />
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Availability Date</label>
                                    <input
                                        type="date"
                                        name="availability_date"
                                        style={{ maxWidth: '300px' }}
                                        value={formData.availability_date}
                                        onChange={handleInputChange}
                                        className="input-field"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Section 3: Financials */}
                            <div className="form-section">
                                <h3 className="section-title">
                                    <span className="section-number">3</span> Financials
                                </h3>

                                <div className="form-row">
                                    <div className="price-input-wrapper">
                                        <Input label="Monthly Rent (₹)" name="price" type="text" value={formData.price} onChange={handleCurrencyChange} required />
                                    </div>
                                    <div className="price-input-wrapper">
                                        <Input label="Security Deposit (Months)" name="security_deposit" type="number" value={formData.security_deposit} onChange={handleInputChange} placeholder="e.g., 2" required />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="price-input-wrapper">
                                        <Input label="Maintenance Charges (₹) (Optional)" name="maintenance_charge" type="text" value={formData.maintenance_charge} onChange={handleCurrencyChange} />
                                    </div>
                                    <div className="input-group">
                                        <label className="input-label">Maintenance Frequency</label>
                                        <select
                                            name="maintenance_frequency"
                                            className="input-field select-field"
                                            value={formData.maintenance_frequency}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Monthly">Monthly</option>
                                            <option value="Quarterly">Quarterly</option>
                                            <option value="Yearly">Yearly</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="checkbox-wrapper">
                                    <input
                                        type="checkbox"
                                        name="negotiable"
                                        id="negotiable"
                                        className="custom-checkbox"
                                        checked={formData.negotiable}
                                        onChange={(e) => setFormData({ ...formData, negotiable: e.target.checked })}
                                    />
                                    <label htmlFor="negotiable">Rent is Negotiable</label>
                                </div>
                            </div>

                            {/* Section 4: Additional Details */}
                            <div className="form-section">
                                <h3 className="section-title">
                                    <span className="section-number">4</span> Amenities & Rules
                                </h3>

                                <div className="input-group">
                                    <label className="input-label">Amenities</label>
                                    <div className="chips-container">
                                        {PROPERTY_AMENITIES.map(amenity => (
                                            <button
                                                key={amenity}
                                                type="button"
                                                className={`chip-btn ${formData.amenities.includes(amenity) ? 'active' : ''}`}
                                                onClick={() => {
                                                    const newAmenities = formData.amenities.includes(amenity)
                                                        ? formData.amenities.filter(a => a !== amenity)
                                                        : [...formData.amenities, amenity];
                                                    setFormData({ ...formData, amenities: newAmenities });
                                                }}
                                            >
                                                {amenity}
                                            </button>
                                        ))}
                                        {/* Show selected custom amenities that aren't in predefined list */}
                                        {formData.amenities.filter(a => !PROPERTY_AMENITIES.includes(a)).map(amenity => (
                                            <button
                                                key={amenity}
                                                type="button"
                                                className="chip-btn active"
                                                onClick={() => {
                                                    const newAmenities = formData.amenities.filter(a => a !== amenity);
                                                    setFormData({ ...formData, amenities: newAmenities });
                                                }}
                                            >
                                                {amenity} ✖
                                            </button>
                                        ))}
                                    </div>
                                    <div className="custom-input-group" style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                                        <input
                                            type="text"
                                            value={customAmenity}
                                            onChange={(e) => setCustomAmenity(e.target.value)}
                                            placeholder="Add other amenity..."
                                            className="input-field"
                                            style={{ flex: 1 }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddCustom('amenity');
                                                }
                                            }}
                                        />
                                        <Button type="button" onClick={() => handleAddCustom('amenity')} style={{ padding: '0 20px' }}>Add</Button>
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label className="input-label">House Rules</label>
                                    <div className="chips-container">
                                        {PROPERTY_RULES.map(rule => (
                                            <button
                                                key={rule}
                                                type="button"
                                                className={`chip-btn ${formData.rules.includes(rule) ? 'active' : ''}`}
                                                onClick={() => {
                                                    const newRules = formData.rules.includes(rule)
                                                        ? formData.rules.filter(r => r !== rule)
                                                        : [...formData.rules, rule];
                                                    setFormData({ ...formData, rules: newRules });
                                                }}
                                            >
                                                {rule}
                                            </button>
                                        ))}
                                        {/* Show selected custom rules that aren't in predefined list */}
                                        {formData.rules.filter(r => !PROPERTY_RULES.includes(r)).map(rule => (
                                            <button
                                                key={rule}
                                                type="button"
                                                className="chip-btn active"
                                                onClick={() => {
                                                    const newRules = formData.rules.filter(r => r !== rule);
                                                    setFormData({ ...formData, rules: newRules });
                                                }}
                                            >
                                                {rule} ✖
                                            </button>
                                        ))}
                                    </div>
                                    <div className="custom-input-group" style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                                        <input
                                            type="text"
                                            value={customRule}
                                            onChange={(e) => setCustomRule(e.target.value)}
                                            placeholder="Add other rule..."
                                            className="input-field"
                                            style={{ flex: 1 }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddCustom('rule');
                                                }
                                            }}
                                        />
                                        <Button type="button" onClick={() => handleAddCustom('rule')} style={{ padding: '0 20px' }}>Add</Button>
                                    </div>
                                </div>

                                <div className="input-group upload-section">
                                    <label className="input-label">Property Images</label>
                                    <div className="upload-zone">
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            id="file-upload"
                                            onChange={handleImageSelect}
                                            className="file-input-hidden"
                                        />
                                        <label htmlFor="file-upload" className="upload-label">
                                            <div className="upload-icon">📁</div>
                                            <p>Click to upload images</p>
                                            <span>JPG, PNG up to 5MB</span>
                                        </label>
                                    </div>

                                    {listingImages.length > 0 && (
                                        <div className="selected-files-count">
                                            {listingImages.length} file{listingImages.length !== 1 ? 's' : ''} selected
                                        </div>
                                    )}

                                    {imagePreviews.length > 0 && (
                                        <div className="image-preview-grid">
                                            {imagePreviews.map((src, index) => (
                                                <div key={index} className="preview-item">
                                                    <img
                                                        src={src}
                                                        alt={`Preview ${index}`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <Button type="button" className="secondary" onClick={resetForm}>Cancel</Button>
                                <Button type="submit">{editingId ? 'Update Listing' : 'Publish Listing'}</Button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading your listings...</p>
                    </div>
                ) : myProperties.length === 0 && !showForm ? (
                    <div className="empty-state">
                        <div className="empty-icon">🏠</div>
                        <h3>No listings yet</h3>
                        <p>Start by adding your first property to the platform.</p>
                        <Button onClick={() => setShowForm(true)}>+ Add New Property</Button>
                    </div>
                ) : (
                    <div className="properties-grid">
                        {myProperties.map(property => (
                            <div key={property.id} className="my-property-card-wrapper fade-in">
                                <PropertyCard property={property} />
                                <div className="card-actions">
                                    <span className={`status-badge ${property.status}`}>{property.status || 'Active'}</span>
                                    <button
                                        className="edit-btn-icon"
                                        onClick={() => handleEdit(property)}
                                        title="Edit Listing"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
                                    >
                                        ✏️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyListings;
